import * as React from 'react';
import { Check } from 'lucide-react';

import { cn } from '../../utils/cn';

export interface StyleTileOption {
    value: string;
    /** Set uppercase over the photo, e.g. `MINIMALIST`. */
    name: string;
    /** The line under it, e.g. `Clean · Pure`. */
    caption?: string;
    image?: string;
}

export interface StyleTileGridProps {
    options: StyleTileOption[];
    values: string[];
    onChange: (values: string[]) => void;
    /** Accessible name for the grid. */
    label: string;
    /** Refuses further selections past this many. Unlimited when omitted. */
    max?: number;
    className?: string;
}

/**
 * The visual multi-select: a photo grid where the picture is the question.
 *
 * Selection both darkens the tile and marks it with a tick. The scrim alone was
 * the earlier treatment — the reasoning being that a tick sits on top of
 * photography that is itself the thing being judged — but a darker version of a
 * photograph is not a state anyone reads as "chosen", and on a dark image there
 * was nothing to see at all. The tick is the same white disc the browse tiles
 * raise in bulk-enquiry mode, so a reader who has selected something anywhere
 * else on the site already knows it.
 *
 * The scrim is carried on a sibling layer so it can cross-fade without the
 * image repainting.
 */
export const StyleTileGrid = ({
    options,
    values,
    onChange,
    label,
    max,
    className,
}: StyleTileGridProps) => {
    const toggle = (value: string) => {
        if (values.includes(value)) {
            onChange(values.filter((entry) => entry !== value));
            return;
        }
        if (max !== undefined && values.length >= max) return;
        onChange([...values, value]);
    };

    return (
        <div
            aria-label={label}
            className={cn('grid grid-cols-2 gap-2.5 md:grid-cols-3', className)}
            role="group"
        >
            {options.map((option) => {
                const checked = values.includes(option.value);
                const atLimit =
                    !checked && max !== undefined && values.length >= max;

                return (
                    <button
                        aria-checked={checked}
                        aria-disabled={atLimit || undefined}
                        className={cn(
                            'group relative aspect-[240/210] overflow-hidden rounded-3 text-left',
                            // A style with no photograph still has to read as a
                            // choice rather than as an image that failed to
                            // load, so it carries a dark ground of its own. The
                            // scrim and the white label are sized for
                            // photography: over the pale surface fill the name
                            // was barely legible, which is how the one style
                            // that shipped without a picture came to look
                            // broken. Every style has one now, so this is the
                            // net under the next one added without.
                            option.image ? 'bg-[#f6f6f6]' : 'bg-[#3d3d3d]',
                            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#181818]',
                            atLimit && 'cursor-not-allowed opacity-50',
                        )}
                        key={option.value}
                        onClick={() => toggle(option.value)}
                        role="checkbox"
                        type="button"
                    >
                        {option.image ? (
                            <img
                                alt=""
                                className="absolute inset-0 size-full object-cover"
                                src={option.image}
                            />
                        ) : null}

                        <span
                            aria-hidden="true"
                            className={cn(
                                'absolute inset-0 transition-colors',
                                checked
                                    ? 'bg-black/45'
                                    : 'bg-gradient-to-t from-black/55 via-black/10 to-transparent group-hover:from-black/65',
                            )}
                        />

                        {/* Not a control of its own: the whole tile toggles,
                            and `aria-checked` on it is what a screen reader
                            reads. This is the visible half of that state. */}
                        <span
                            aria-hidden="true"
                            className={cn(
                                'absolute top-1/2 left-1/2 flex size-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[var(--radius-btn-m)] bg-white text-[#181818] shadow-[0_6px_20px_rgba(0,0,0,0.25)] transition-[opacity,scale] duration-200',
                                checked
                                    ? 'scale-100 opacity-100'
                                    : 'scale-75 opacity-0',
                            )}
                        >
                            <Check className="size-5" />
                        </span>

                        <span className="absolute inset-x-3.5 bottom-3.5 flex flex-col gap-1">
                            <span className="font-sans text-3.25 leading-none font-medium tracking-[0.03em] text-white uppercase">
                                {option.name}
                            </span>
                            {option.caption ? (
                                <span className="font-sans text-3 leading-none tracking-[-0.01em] text-white/80">
                                    {option.caption}
                                </span>
                            ) : null}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};
